import React, { useState } from 'react';
import { formatDate } from '../utils/formatDate';
import { formatValue } from '../utils/formatValue';
import type { EntityType, Entity, Attribute } from '../types';
import { useEditMode } from '../context/EditModeContext';
import './EntityDetail.css';

interface AttributesTabProps {
  entity: Entity;
  entityType?: EntityType;
  attributes: Attribute[];
  onEntityClick: (entityType: EntityType, entityId: number) => void;
  onAttributeUpdate?: (attributeName: string, value: any) => Promise<void>;
}

const ExpandableJson: React.FC<{ value: string }> = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = value.length > 120;

  if (!isLong) {
    return <code className="json-value json-inline">{value}</code>;
  }

  if (!expanded) {
    return (
      <span className="json-collapsed-row">
        <code className="json-value json-inline json-truncated">{value.slice(0, 80)}…</code>
        <button className="json-toggle-btn" onClick={() => setExpanded(true)}>Show</button>
      </span>
    );
  }

  return (
    <div className="json-expandable">
      <pre className="json-value json-expanded">{value}</pre>
      <button className="json-toggle-btn" onClick={() => setExpanded(false)}>Hide</button>
    </div>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailListEditor: React.FC<{
  value: unknown;
  onSave: (emails: string[]) => Promise<void>;
  onCancel: () => void;
}> = ({ value, onSave, onCancel }) => {
  const initial = Array.isArray(value)
    ? Array.from(
        new Set(
          (value as unknown[])
            .filter((v): v is string => typeof v === 'string')
            .map((v) => v.trim())
            .filter(Boolean),
        ),
      )
    : [];
  const [emails, setEmails] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addDraft = () => {
    const candidate = draft.trim();
    if (candidate === '') return;
    if (!EMAIL_RE.test(candidate)) { setError(`Invalid email: ${candidate}`); return; }
    if (emails.includes(candidate)) { setDraft(''); return; }
    setEmails([...emails, candidate]);
    setDraft('');
    setError(null);
  };

  const removeAt = (i: number) => setEmails(emails.filter((_, idx) => idx !== i));

  const save = async () => {
    setError(null);
    // Fold in any address typed but not yet added, so clicking Save doesn't drop it.
    const candidate = draft.trim();
    let finalEmails = emails;
    if (candidate !== '') {
      if (!EMAIL_RE.test(candidate)) { setError(`Invalid email: ${candidate}`); return; }
      finalEmails = emails.includes(candidate) ? emails : [...emails, candidate];
    }
    try {
      await onSave(finalEmails);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <div className="email-list-editor">
      <div className="email-chips">
        {emails.map((email, i) => (
          <span key={`${email}-${i}`} className="email-chip">
            {email}
            <button type="button" className="email-chip-remove" onClick={() => removeAt(i)} aria-label={`Remove ${email}`}>×</button>
          </span>
        ))}
      </div>
      <input
        type="email"
        className="inline-edit-input"
        placeholder="Add email and press Enter"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); addDraft(); }
          if (e.key === 'Escape') onCancel();
        }}
        autoFocus
      />
      <div className="email-list-actions">
        <button type="button" onClick={addDraft}>Add</button>
        <button type="button" onClick={save}>Save</button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
      {error && <span className="email-list-error">{error}</span>}
    </div>
  );
};

export const EntityDetailAttributesTab: React.FC<AttributesTabProps> = ({
  entity,
  entityType,
  attributes,
  onEntityClick,
  onAttributeUpdate,
}) => {
  const [showEmpty, setShowEmpty] = useState(false);
  const { isEditMode } = useEditMode();
  const [editingAttr, setEditingAttr] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editError, setEditError] = useState<string | null>(null);

  const getAttributeValue = (attributeName: string): string | number | boolean | Date | Record<string, unknown> | null | undefined => {
    return entity?.attributes?.[attributeName];
  };

  const sortedAttributes = [...attributes].sort((a, b) => {
    const aValue = entity?.attributes?.[a.name];
    const bValue = entity?.attributes?.[b.name];
    const aHasValue = aValue !== null && aValue !== undefined;
    const bHasValue = bValue !== null && bValue !== undefined;

    if (aHasValue && !bHasValue) return -1;
    if (!aHasValue && bHasValue) return 1;

    return a.name.localeCompare(b.name);
  });

  const populatedAttrs = sortedAttributes.filter(a => {
    const v = entity?.attributes?.[a.name];
    return v !== null && v !== undefined;
  });
  const emptyAttrs = sortedAttributes.filter(a => {
    const v = entity?.attributes?.[a.name];
    return v === null || v === undefined;
  });

  const startEditing = (attr: Attribute) => {
    const value = getAttributeValue(attr.name);
    setEditingAttr(attr.name);
    setEditValue(value != null ? String(value) : '');
  };

  const cancelEditing = () => {
    setEditingAttr(null);
    setEditValue('');
  };

  const commitEdit = async (attr: Attribute) => {
    if (!onAttributeUpdate) return;
    let parsedValue: any = editValue;
    if (editValue === '') {
      parsedValue = null;
    } else if (attr.data_type === 'number') {
      parsedValue = Number(editValue);
    } else if (attr.data_type === 'boolean') {
      parsedValue = editValue === 'true';
    }
    setEditError(null);
    try {
      await onAttributeUpdate(attr.name, parsedValue);
      setEditingAttr(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const renderEditableValue = (attr: Attribute) => {
    if (attr.data_type === 'boolean') {
      return (
        <select
          className="inline-edit-input"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => commitEdit(attr)}
          autoFocus
        >
          <option value="">—</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }
    if (attr.data_type === 'date') {
      return (
        <input
          type="date"
          className="inline-edit-input"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={() => commitEdit(attr)}
          onKeyDown={e => { if (e.key === 'Enter') commitEdit(attr); if (e.key === 'Escape') cancelEditing(); }}
          autoFocus
        />
      );
    }
    return (
      <input
        type={attr.data_type === 'number' ? 'number' : 'text'}
        className="inline-edit-input"
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onBlur={() => commitEdit(attr)}
        onKeyDown={e => { if (e.key === 'Enter') commitEdit(attr); if (e.key === 'Escape') cancelEditing(); }}
        autoFocus
      />
    );
  };

  const renderRow = (attr: Attribute) => {
    const value = getAttributeValue(attr.name);
    const hasValue = value !== null && value !== undefined;
    const isReference = attr.referenced_entity_type !== undefined;
    const isEditing = editingAttr === attr.name;
    const isEmailList = attr.name === 'notification_emails';
    const canEdit = isEditMode && onAttributeUpdate && (attr.data_type !== 'json' || isEmailList) && !isReference;

    return (
      <tr key={attr.id || attr.name} className={!hasValue ? 'null-row' : ''}>
        <td className="attr-name-cell">
          <span className="attr-name-raw">{attr.name}</span>
          {isReference && (
            <span className="reference-indicator" title={`References ${attr.referenced_entity_type}`}>
              {' '}→ {attr.referenced_entity_type}
            </span>
          )}
        </td>
        <td>
          <span className="data-type-badge" data-type={attr.data_type}>
            {attr.data_type}
          </span>
        </td>
        <td className={`attr-value-cell ${!hasValue ? 'empty' : ''}${canEdit ? ' editable' : ''}`}>
          {isEditing && attr.name === 'notification_emails' ? (
            <EmailListEditor
              value={value}
              onSave={async (emails) => {
                if (!onAttributeUpdate) return;
                await onAttributeUpdate('notification_emails', emails.length > 0 ? emails : null);
                setEditingAttr(null);
              }}
              onCancel={cancelEditing}
            />
          ) : isEditing ? (
            <>
              {renderEditableValue(attr)}
              {editError && <span style={{ color: 'var(--accent-red, #ef4444)', fontSize: '0.75rem', marginLeft: '0.5rem' }}>{editError}</span>}
            </>
          ) : hasValue ? (
            isEmailList ? (
              (() => {
                const emailList = Array.isArray(value)
                  ? (value as unknown[]).filter((v): v is string => typeof v === 'string')
                  : [];
                return (
                  <span
                    onClick={canEdit ? () => startEditing(attr) : undefined}
                    className={canEdit ? 'editable-value email-chips' : 'email-chips'}
                  >
                    {emailList.length > 0
                      ? emailList.map((email, i) => (
                          <span key={`${email}-${i}`} className="email-chip">{email}</span>
                        ))
                      : '-'}
                  </span>
                );
              })()
            ) : attr.data_type === 'json' ? (
              <ExpandableJson value={formatValue(value, { dataType: attr.data_type })} />
            ) : isReference && typeof value === 'number' ? (
              <button
                className="entity-link"
                onClick={() => onEntityClick(attr.referenced_entity_type!, value)}
                title={`View ${attr.referenced_entity_type} ${value}`}
              >
                {value}
              </button>
            ) : (
              <span
                onClick={canEdit ? () => startEditing(attr) : undefined}
                className={canEdit ? 'editable-value' : undefined}
              >{formatValue(value, { dataType: attr.data_type })}</span>
            )
          ) : (
            <span
              className={`empty-value${canEdit ? ' editable-value' : ''}`}
              onClick={canEdit ? () => startEditing(attr) : undefined}
            >-</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="entity-meta-strip">
        <span className="meta-item">ID: {entity.id}</span>
        {entity.created_at && <span className="meta-item">Created: {formatDate(entity.created_at)}</span>}
        {entity.updated_at && <span className="meta-item">Updated: {formatDate(entity.updated_at)}</span>}
      </div>

      <div className="entity-detail-section">
        {sortedAttributes.length === 0 ? (
          <p className="no-attributes">No attributes defined for this entity type.</p>
        ) : (
          <>
            <table className="entity-attributes-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {populatedAttrs.map(renderRow)}
                {showEmpty && emptyAttrs.map(renderRow)}
              </tbody>
            </table>
            {emptyAttrs.length > 0 && (
              <button
                className="toggle-empty-attrs-btn"
                onClick={() => setShowEmpty(!showEmpty)}
              >
                {showEmpty
                  ? `Hide ${emptyAttrs.length} empty attribute${emptyAttrs.length !== 1 ? 's' : ''}`
                  : `Show ${emptyAttrs.length} empty attribute${emptyAttrs.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
};
